/**
 * Data Prep Engine
 * 
 * Applies data preparation steps sequentially to transform raw dataset data.
 * Features:
 * - Per-step StepResult with data, warnings, and stats
 * - Column schema with stats and warnings after each step
 * - Memoization/caching for performance
 * - Soft row-limit for preview mode
 * - Full transform option for export
 */

import {
    ColumnSchema,
    ColumnStats,
    DataPrepStep,
    DataPrepStepWithMeta,
    DataPrepWarning,
    StepResult,
    DataPrepPipelineResult,
    DataPrepConfig,
    DEFAULT_DATA_PREP_CONFIG,
    NullHandlingStep,
    DuplicateHandlingStep,
    StringCleaningStep,
    DataTypeCastingStep,
    NormalizationStep,
    CalculatedColumnStep,
    ColumnManagementStep
} from './dataPrepTypes';

import { parseExpression, evaluateExpression } from './expressionParser';

// ============================================================================
// Column Schema & Stats Computation
// ============================================================================

function computeColumnStats(data: Record<string, any>[], columnName: string, columnType: 'string' | 'number' | 'date' | 'boolean'): ColumnStats {
    let nullCount = 0;
    const values: (string | number | Date)[] = [];
    const valueCounts = new Map<string, number>();

    for (const row of data) {
        const val = row[columnName];
        if (val === null || val === undefined || val === '') {
            nullCount++;
        } else {
            values.push(val);
            const key = String(val);
            valueCounts.set(key, (valueCounts.get(key) || 0) + 1);
        }
    }

    const stats: ColumnStats = {
        nullCount,
        uniqueCount: valueCounts.size
    };

    // Find mode
    if (valueCounts.size > 0) {
        let maxCount = 0;
        let modeValue: string = '';
        for (const [val, count] of valueCounts) {
            if (count > maxCount) {
                maxCount = count;
                modeValue = val;
            }
        }
        stats.mode = columnType === 'number' ? parseFloat(modeValue) : modeValue;
    }

    // Numeric-specific stats
    if (columnType === 'number') {
        const numericValues = values
            .map(v => typeof v === 'number' ? v : parseFloat(String(v)))
            .filter(v => !isNaN(v));

        if (numericValues.length > 0) {
            numericValues.sort((a, b) => a - b);
            stats.min = numericValues[0];
            stats.max = numericValues[numericValues.length - 1];
            stats.mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;

            const mid = Math.floor(numericValues.length / 2);
            stats.median = numericValues.length % 2 === 0
                ? (numericValues[mid - 1] + numericValues[mid]) / 2
                : numericValues[mid];
        }
    }

    // Date-specific stats
    if (columnType === 'date') {
        const dateValues = values
            .map(v => v instanceof Date ? v : new Date(String(v)))
            .filter(v => !isNaN(v.getTime()))
            .sort((a, b) => a.getTime() - b.getTime());

        if (dateValues.length > 0) {
            stats.min = dateValues[0];
            stats.max = dateValues[dateValues.length - 1];
        }
    }

    return stats;
}

export function computeSchema(
    data: Record<string, any>[],
    baseSchema: Array<{ name: string; type: 'string' | 'number' | 'date' | 'boolean'; hidden?: boolean }>
): ColumnSchema[] {
    return baseSchema.map(col => {
        const stats = computeColumnStats(data, col.name, col.type);
        const warnings: string[] = [];

        // Generate warnings based on stats
        if (stats.nullCount > 0) {
            const pct = ((stats.nullCount / data.length) * 100).toFixed(1);
            warnings.push(`${stats.nullCount} null values (${pct}%)`);
        }

        if (col.type === 'number' && stats.uniqueCount === 1 && data.length > 1) {
            warnings.push('All values are identical');
        }

        return {
            name: col.name,
            type: col.type,
            stats,
            warnings,
            hidden: col.hidden
        };
    });
}

// ============================================================================
// Step Executors
// ============================================================================

function executeNullHandling(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: NullHandlingStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    const columnSchema = schema.find(c => c.name === step.column);
    if (!columnSchema) {
        warnings.push({
            stepId,
            message: `Column "${step.column}" not found`,
            severity: 'error'
        });
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    let result: Record<string, any>[];
    let rowsRemoved = 0;

    if (step.action === 'remove-rows') {
        result = data.filter(row => {
            const val = row[step.column];
            const isNull = val === null || val === undefined || val === '';
            if (isNull) rowsRemoved++;
            return !isNull;
        });
    } else {
        // Replace nulls
        let replacementValue: any;

        switch (step.replacement) {
            case '0':
                replacementValue = 0;
                break;
            case 'mean':
                replacementValue = columnSchema.stats.mean ?? 0;
                break;
            case 'median':
                replacementValue = columnSchema.stats.median ?? 0;
                break;
            case 'mode':
                replacementValue = columnSchema.stats.mode ?? '';
                break;
            case 'custom':
                replacementValue = step.customValue ?? '';
                break;
            default:
                replacementValue = '';
        }

        let replacedCount = 0;
        result = data.map(row => {
            const val = row[step.column];
            if (val === null || val === undefined || val === '') {
                replacedCount++;
                return { ...row, [step.column]: replacementValue };
            }
            return row;
        });

        if (replacedCount > 0) {
            warnings.push({
                stepId,
                column: step.column,
                message: `Replaced ${replacedCount} null values with ${step.replacement === 'custom' ? `"${replacementValue}"` : step.replacement}`,
                severity: 'info',
                affectedRows: replacedCount
            });
        }
    }

    const newSchema = computeSchema(result, schema);

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved,
            columnsAdded: 0,
            columnsModified: rowsRemoved === 0 ? 1 : 0,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeDuplicateHandling(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: DuplicateHandlingStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    const keyColumns = step.keyColumns.length > 0
        ? step.keyColumns
        : schema.map(c => c.name);

    const seen = new Set<string>();
    const result: Record<string, any>[] = [];

    for (const row of data) {
        const key = keyColumns.map(col => String(row[col] ?? '')).join('::');
        if (!seen.has(key)) {
            seen.add(key);
            result.push(row);
        }
    }

    const rowsRemoved = inputRows - result.length;
    if (rowsRemoved > 0) {
        warnings.push({
            stepId,
            message: `Removed ${rowsRemoved} duplicate rows`,
            severity: 'info',
            affectedRows: rowsRemoved
        });
    }

    const newSchema = computeSchema(result, schema);

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved,
            columnsAdded: 0,
            columnsModified: 0,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeStringCleaning(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: StringCleaningStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    const columnSchema = schema.find(c => c.name === step.column);
    if (!columnSchema) {
        warnings.push({
            stepId,
            message: `Column "${step.column}" not found`,
            severity: 'error'
        });
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    let modifiedCount = 0;
    const result = data.map(row => {
        let val = row[step.column];
        if (val === null || val === undefined) return row;

        const originalVal = val;
        val = String(val);

        for (const op of step.operations) {
            switch (op) {
                case 'trim':
                    val = val.trim();
                    break;
                case 'lowercase':
                    val = val.toLowerCase();
                    break;
                case 'uppercase':
                    val = val.toUpperCase();
                    break;
            }
        }

        if (step.findReplace && step.findReplace.find) {
            val = val.split(step.findReplace.find).join(step.findReplace.replace);
        }

        if (val !== String(originalVal)) {
            modifiedCount++;
        }

        return { ...row, [step.column]: val };
    });

    if (modifiedCount > 0) {
        warnings.push({
            stepId,
            column: step.column,
            message: `Modified ${modifiedCount} values`,
            severity: 'info',
            affectedRows: modifiedCount
        });
    }

    const newSchema = computeSchema(result, schema);

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved: 0,
            columnsAdded: 0,
            columnsModified: 1,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeTypeCasting(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: DataTypeCastingStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    const colIdx = schema.findIndex(c => c.name === step.column);
    if (colIdx === -1) {
        warnings.push({
            stepId,
            message: `Column "${step.column}" not found`,
            severity: 'error'
        });
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    let invalidCount = 0;
    const validRows: Record<string, any>[] = [];

    for (const row of data) {
        const val = row[step.column];
        let converted: any = val;
        let isValid = true;

        if (val !== null && val !== undefined && val !== '') {
            switch (step.targetType) {
                case 'number': {
                    const num = parseFloat(String(val));
                    if (isNaN(num)) {
                        isValid = false;
                        converted = null;
                    } else {
                        converted = num;
                    }
                    break;
                }
                case 'date': {
                    const date = new Date(val);
                    if (isNaN(date.getTime())) {
                        isValid = false;
                        converted = null;
                    } else {
                        converted = date.toISOString();
                    }
                    break;
                }
                case 'string':
                    converted = String(val);
                    break;
            }
        }

        if (!isValid) {
            invalidCount++;
            if (step.removeInvalid) continue; // Skip row
        }

        validRows.push({ ...row, [step.column]: converted });
    }

    const rowsRemoved = inputRows - validRows.length;

    if (invalidCount > 0) {
        warnings.push({
            stepId,
            column: step.column,
            message: `${invalidCount} values could not be converted to ${step.targetType}${step.removeInvalid ? ` (${rowsRemoved} rows removed)` : ''}`,
            severity: 'warning',
            affectedRows: invalidCount
        });
    }

    // Update schema type
    const newSchemaBase = schema.map((col, idx) => {
        if (idx === colIdx) {
            return { ...col, type: step.targetType as any };
        }
        return col;
    });

    const newSchema = computeSchema(validRows, newSchemaBase);

    return {
        data: validRows,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: validRows.length,
            rowsRemoved,
            columnsAdded: 0,
            columnsModified: 1,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeNormalization(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: NormalizationStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;
    let columnsAdded = 0;

    // Validate columns exist and are numeric
    const validColumns: string[] = [];
    for (const colName of step.columns) {
        const col = schema.find(c => c.name === colName);
        if (!col) {
            warnings.push({
                stepId,
                message: `Column "${colName}" not found`,
                severity: 'error'
            });
        } else if (col.type !== 'number') {
            warnings.push({
                stepId,
                column: colName,
                message: `Column "${colName}" is not numeric, skipping`,
                severity: 'warning'
            });
        } else {
            validColumns.push(colName);
        }
    }

    if (validColumns.length === 0) {
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    // Compute stats for normalization
    const columnStats: Record<string, { min: number; max: number; mean: number; std: number }> = {};

    for (const colName of validColumns) {
        const values = data
            .map(row => parseFloat(row[colName]))
            .filter(v => !isNaN(v));

        if (values.length === 0) {
            columnStats[colName] = { min: 0, max: 0, mean: 0, std: 1 };
            continue;
        }

        const min = Math.min(...values);
        const max = Math.max(...values);
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
        const std = Math.sqrt(variance) || 1; // Avoid division by zero

        columnStats[colName] = { min, max, mean, std };
    }

    // Apply normalization
    const result = data.map((row, rowIdx) => {
        const newRow = { ...row };

        for (const colName of validColumns) {
            const val = parseFloat(row[colName]);
            if (isNaN(val)) continue;

            const stats = columnStats[colName];
            let normalized: number | null = null;

            switch (step.method) {
                case 'min-max':
                    normalized = stats.max === stats.min ? 0 : (val - stats.min) / (stats.max - stats.min);
                    break;
                case 'z-score':
                    normalized = (val - stats.mean) / stats.std;
                    break;
                case 'log':
                    if (val <= 0) {
                        if (rowIdx === 0) {
                            warnings.push({
                                stepId,
                                column: colName,
                                message: `Log transform skipped for non-positive values in "${colName}"`,
                                severity: 'warning'
                            });
                        }
                        normalized = null;
                    } else {
                        normalized = Math.log(val);
                    }
                    break;
                case 'abs':
                    normalized = Math.abs(val);
                    break;
                case 'pct-change':
                    // Need previous row value
                    if (rowIdx === 0) {
                        normalized = 0;
                    } else {
                        const prevVal = parseFloat(data[rowIdx - 1][colName]);
                        if (isNaN(prevVal) || prevVal === 0) {
                            normalized = 0;
                        } else {
                            normalized = ((val - prevVal) / prevVal) * 100;
                        }
                    }
                    break;
            }

            const targetCol = step.createNewColumn
                ? `${colName}${step.newColumnSuffix || '_normalized'}`
                : colName;

            if (normalized !== null) {
                newRow[targetCol] = normalized;
            }
        }

        return newRow;
    });

    // Update schema
    let newSchemaBase = [...schema];
    if (step.createNewColumn) {
        for (const colName of validColumns) {
            const newColName = `${colName}${step.newColumnSuffix || '_normalized'}`;
            newSchemaBase.push({ name: newColName, type: 'number', stats: {} as any, warnings: [] });
            columnsAdded++;
        }
    }

    const newSchema = computeSchema(result, newSchemaBase);

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved: 0,
            columnsAdded,
            columnsModified: step.createNewColumn ? 0 : validColumns.length,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeCalculatedColumn(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: CalculatedColumnStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    // Parse the formula
    const columnNames = schema.map(c => c.name);
    const parseResult = parseExpression(step.formula, columnNames);

    if (!parseResult.success) {
        warnings.push({
            stepId,
            message: `Formula error: ${parseResult.error}`,
            severity: 'error'
        });
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    // Validate referenced columns exist
    for (const col of parseResult.referencedColumns) {
        // Try case-insensitive match
        const found = columnNames.find(c => c.toLowerCase() === col.toLowerCase());
        if (!found) {
            warnings.push({
                stepId,
                message: `Unknown column "${col}" in formula`,
                severity: 'error'
            });
            return {
                data,
                schema,
                warnings,
                stats: {
                    inputRows,
                    outputRows: data.length,
                    rowsRemoved: 0,
                    columnsAdded: 0,
                    columnsModified: 0,
                    executionTimeMs: performance.now() - startTime
                }
            };
        }
    }

    // Evaluate formula for each row
    let evalErrors = 0;
    const result = data.map(row => {
        try {
            const value = evaluateExpression(parseResult.expression!, row);
            return { ...row, [step.newColumnName]: value };
        } catch (e) {
            evalErrors++;
            return { ...row, [step.newColumnName]: null };
        }
    });

    if (evalErrors > 0) {
        warnings.push({
            stepId,
            message: `${evalErrors} rows had evaluation errors`,
            severity: 'warning',
            affectedRows: evalErrors
        });
    }

    // Determine the type of the new column
    const sampleValues = result.slice(0, 100).map(r => r[step.newColumnName]).filter(v => v !== null);
    let newColType: 'number' | 'string' | 'boolean' = 'number';
    if (sampleValues.some(v => typeof v === 'string')) {
        newColType = 'string';
    } else if (sampleValues.every(v => typeof v === 'boolean')) {
        newColType = 'boolean';
    }

    const newSchemaBase = [
        ...schema,
        { name: step.newColumnName, type: newColType, stats: {} as any, warnings: [] }
    ];

    const newSchema = computeSchema(result, newSchemaBase);

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved: 0,
            columnsAdded: 1,
            columnsModified: 0,
            executionTimeMs: performance.now() - startTime
        }
    };
}

function executeColumnManagement(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    step: ColumnManagementStep,
    stepId: string
): StepResult {
    const startTime = performance.now();
    const warnings: DataPrepWarning[] = [];
    const inputRows = data.length;

    const colIdx = schema.findIndex(c => c.name === step.column);
    if (colIdx === -1) {
        warnings.push({
            stepId,
            message: `Column "${step.column}" not found`,
            severity: 'error'
        });
        return {
            data,
            schema,
            warnings,
            stats: {
                inputRows,
                outputRows: data.length,
                rowsRemoved: 0,
                columnsAdded: 0,
                columnsModified: 0,
                executionTimeMs: performance.now() - startTime
            }
        };
    }

    let result = data;
    let newSchema = [...schema];

    switch (step.action) {
        case 'rename':
            if (step.newName) {
                // Rename in data
                result = data.map(row => {
                    const { [step.column]: oldVal, ...rest } = row;
                    return { ...rest, [step.newName!]: oldVal };
                });
                // Rename in schema
                newSchema = schema.map((col, idx) =>
                    idx === colIdx ? { ...col, name: step.newName! } : col
                );
            }
            break;

        case 'hide':
            newSchema = schema.map((col, idx) =>
                idx === colIdx ? { ...col, hidden: step.hidden !== false } : col
            );
            break;

        case 'delete':
            result = data.map(row => {
                const { [step.column]: _, ...rest } = row;
                return rest;
            });
            newSchema = schema.filter((_, idx) => idx !== colIdx);
            break;

        case 'reorder':
            if (step.newPosition !== undefined) {
                const [col] = newSchema.splice(colIdx, 1);
                const insertAt = Math.min(Math.max(0, step.newPosition), newSchema.length);
                newSchema.splice(insertAt, 0, col);
            }
            break;
    }

    // Recompute stats if data changed
    if (step.action === 'rename' || step.action === 'delete') {
        newSchema = computeSchema(result, newSchema);
    }

    return {
        data: result,
        schema: newSchema,
        warnings,
        stats: {
            inputRows,
            outputRows: result.length,
            rowsRemoved: step.action === 'delete' ? 0 : 0,
            columnsAdded: 0,
            columnsModified: 1,
            executionTimeMs: performance.now() - startTime
        }
    };
}

// ============================================================================
// Step Router
// ============================================================================

function executeStep(
    data: Record<string, any>[],
    schema: ColumnSchema[],
    stepWithMeta: DataPrepStepWithMeta
): StepResult {
    const { id: stepId, step } = stepWithMeta;

    switch (step.type) {
        case 'null-handling':
            return executeNullHandling(data, schema, step, stepId);
        case 'duplicate-handling':
            return executeDuplicateHandling(data, schema, step, stepId);
        case 'string-cleaning':
            return executeStringCleaning(data, schema, step, stepId);
        case 'type-casting':
            return executeTypeCasting(data, schema, step, stepId);
        case 'normalization':
            return executeNormalization(data, schema, step, stepId);
        case 'calculated-column':
            return executeCalculatedColumn(data, schema, step, stepId);
        case 'column-management':
            return executeColumnManagement(data, schema, step, stepId);
        default:
            // Unknown step type
            return {
                data,
                schema,
                warnings: [{
                    stepId,
                    message: `Unknown step type: ${(step as any).type}`,
                    severity: 'error'
                }],
                stats: {
                    inputRows: data.length,
                    outputRows: data.length,
                    rowsRemoved: 0,
                    columnsAdded: 0,
                    columnsModified: 0,
                    executionTimeMs: 0
                }
            };
    }
}

// ============================================================================
// Memoization Cache
// ============================================================================

interface CacheEntry {
    hash: string;
    result: DataPrepPipelineResult;
    timestamp: number;
}

const pipelineCache = new Map<string, CacheEntry>();
const CACHE_MAX_SIZE = 20;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function computeStepsHash(steps: DataPrepStepWithMeta[]): string {
    return JSON.stringify(
        steps
            .filter(s => s.enabled)
            .sort((a, b) => a.order - b.order)
            .map(s => ({ id: s.id, step: s.step, enabled: s.enabled }))
    );
}

function getCachedResult(datasetId: string, stepsHash: string): DataPrepPipelineResult | null {
    const key = datasetId;
    const entry = pipelineCache.get(key);

    if (entry && entry.hash === stepsHash && Date.now() - entry.timestamp < CACHE_TTL_MS) {
        return { ...entry.result, stats: { ...entry.result.stats, wasCached: true } };
    }

    return null;
}

function cacheResult(datasetId: string, stepsHash: string, result: DataPrepPipelineResult): void {
    // Evict old entries if cache is full
    if (pipelineCache.size >= CACHE_MAX_SIZE) {
        const oldest = Array.from(pipelineCache.entries())
            .sort((a, b) => a[1].timestamp - b[1].timestamp)[0];
        if (oldest) pipelineCache.delete(oldest[0]);
    }

    pipelineCache.set(datasetId, {
        hash: stepsHash,
        result,
        timestamp: Date.now()
    });
}

export function invalidateCache(datasetId: string): void {
    pipelineCache.delete(datasetId);
}

export function clearAllCache(): void {
    pipelineCache.clear();
}

// ============================================================================
// Main Pipeline Function
// ============================================================================

export function applyDataPrepPipeline(
    datasetId: string,
    rawData: Record<string, any>[],
    baseSchema: Array<{ name: string; type: 'string' | 'number' | 'date' | 'boolean' }>,
    steps: DataPrepStepWithMeta[],
    config: Partial<DataPrepConfig> = {}
): DataPrepPipelineResult {
    const finalConfig: DataPrepConfig = { ...DEFAULT_DATA_PREP_CONFIG, ...config };
    const pipelineStartTime = performance.now();

    // Check cache
    const stepsHash = computeStepsHash(steps);
    if (finalConfig.enableCaching) {
        const cached = getCachedResult(datasetId, stepsHash);
        if (cached) {
            return cached;
        }
    }

    // Apply row limit for preview
    const isPreviewMode = rawData.length > finalConfig.previewRowLimit;
    const dataToProcess = isPreviewMode
        ? rawData.slice(0, finalConfig.previewRowLimit)
        : rawData;

    // Compute initial schema
    let currentData = dataToProcess;
    let currentSchema = computeSchema(dataToProcess, baseSchema);

    // Sort and filter enabled steps
    const enabledSteps = steps
        .filter(s => s.enabled)
        .sort((a, b) => a.order - b.order);

    const allWarnings: DataPrepWarning[] = [];
    const stepResults = new Map<string, StepResult>();

    // Execute each step
    for (const stepWithMeta of enabledSteps) {
        const result = executeStep(currentData, currentSchema, stepWithMeta);

        stepResults.set(stepWithMeta.id, result);
        allWarnings.push(...result.warnings);

        currentData = result.data;
        currentSchema = result.schema;

        // Stop on error if configured
        if (finalConfig.stopOnError && result.warnings.some(w => w.severity === 'error')) {
            break;
        }
    }

    const totalTime = performance.now() - pipelineStartTime;

    const pipelineResult: DataPrepPipelineResult = {
        data: currentData,
        schema: currentSchema,
        warnings: allWarnings,
        stepResults,
        stats: {
            totalInputRows: rawData.length,
            totalOutputRows: currentData.length,
            totalExecutionTimeMs: totalTime,
            isPreviewMode,
            wasCached: false
        }
    };

    // Cache the result
    if (finalConfig.enableCaching) {
        cacheResult(datasetId, stepsHash, pipelineResult);
    }

    return pipelineResult;
}

// ============================================================================
// Full Transform (for Export)
// ============================================================================

export function applyFullTransform(
    rawData: Record<string, any>[],
    baseSchema: Array<{ name: string; type: 'string' | 'number' | 'date' | 'boolean' }>,
    steps: DataPrepStepWithMeta[]
): DataPrepPipelineResult {
    // Run without preview limit and without caching
    return applyDataPrepPipeline('__export__', rawData, baseSchema, steps, {
        previewRowLimit: Infinity,
        enableCaching: false
    });
}
